import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { AlertColor, Box, Snackbar as MuiSnackbar, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SnackbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '4px',
  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
  padding: '12px 16px',
  minWidth: '300px',
  maxWidth: '400px',
  position: 'relative',
}));

const VerticalLine = styled(Box)<{ variant: AlertColor }>(({ theme, variant }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '4px',
  borderTopLeftRadius: '4px',
  borderBottomLeftRadius: '4px',
  backgroundColor: 
    variant === 'success' ? '#2e7d32' : 
    variant === 'warning' ? '#ed6c02' : 
    variant === 'error' ? '#d32f2f' : 
    '#0288d1',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  marginRight: '12px',
  display: 'flex',
  alignItems: 'center',
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  marginLeft: '16px',
}));

export type SnackbarVariant = 'success' | 'warning' | 'danger' | 'info';

export interface SnackbarProps {
  open: boolean;
  message?: string;
  variant?: AlertColor;
  autoHideDuration?: number;
  onClose?: () => void;
  action?: React.ReactNode;
}

const KeydriveSnackbar: React.FC<SnackbarProps> = ({
  open,
  message,
  variant = 'success',
  autoHideDuration = 6000,
  onClose,
  action
}) => {

    const { t } = useTranslation();

    const getIcon = () => {
        switch (variant) {
            case 'success':
                return <CheckCircleIcon fontSize="small" style={{ color: '#2e7d32' }} />;
            case 'warning':
                return <WarningIcon fontSize="small" style={{ color: '#ed6c02' }} />;
            case 'error':
                return <ErrorIcon fontSize="small" style={{ color: '#d32f2f' }} />;
            case 'info':
                return <InfoIcon fontSize="small" style={{ color: '#0288d1' }} />;
            default:
                return <CheckCircleIcon fontSize="small" style={{ color: '#2e7d32' }} />;
            }
    };

  return (
    <MuiSnackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <SnackbarContainer>
        <VerticalLine variant={variant} />
        <IconContainer>
          {getIcon()}
        </IconContainer>
        <MessageContainer>
          <Typography variant="body2" component="span" fontWeight="500">
            {variant === 'success' && t("Snackbar.success")}
            {variant === 'warning' && t("Snackbar.atention")}
            {variant === 'error' && t("Snackbar.error")}
            {variant === 'info' && t("Snackbar.info")}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
            {message}
          </Typography>
        </MessageContainer>
        {action && (
          <ActionContainer>
            {action}
          </ActionContainer>
        )}
      </SnackbarContainer>
    </MuiSnackbar>
  );
};

export default KeydriveSnackbar;